export type SeatLevelNote = {
  id: string;
  userId: string;
  teamId: string;
  teamName?: string;
  section: string;
  row: string;
  seatStart: number;
  seatEnd: number;
  internalNote: string;
  externalNote: string;
};

export type SeatLocation = {
  userId: string;
  teamId: string;
  teamName?: string;
  eventName?: string;
  section: string;
  row: string;
  seatStart: number;
  seatEnd: number;
};

const normalizedText = (value: unknown) => String(value ?? '').trim().toLowerCase();

export const withSeatNoteTeamNames = (
  notes: SeatLevelNote[],
  teams: Array<{ id?: string | null; name?: string | null }>,
): SeatLevelNote[] => {
  const names = new Map(
    teams
      .map((team) => [normalizedText(team.id), String(team.name || '').trim()] as const)
      .filter((entry) => entry[0] && entry[1]),
  );
  return notes.map((note) => ({
    ...note,
    teamName: names.get(normalizedText(note.teamId)) || note.teamName || '',
  }));
};

/** A named team matches the requested team, or either side of the event title. */
export const seatNoteMatchesTeam = (
  note: Pick<SeatLevelNote, 'teamId' | 'teamName'>,
  location: { teamId?: string; teamName?: string; eventName?: string },
): boolean => {
  const noteName = normalizedText(note.teamName);
  const eventName = normalizedText(location.eventName);
  if (noteName && eventName) return eventName.includes(noteName);
  const requestedName = normalizedText(location.teamName);
  if (noteName && requestedName) return noteName === requestedName;
  return Boolean(location.teamId) && normalizedText(note.teamId) === normalizedText(location.teamId);
};

export const findMatchingSeatLevelNote = (
  notes: SeatLevelNote[],
  location: SeatLocation,
): SeatLevelNote | null => {
  const matching = notes.filter((note) => {
    if (normalizedText(note.userId) !== normalizedText(location.userId)) return false;
    if (!seatNoteMatchesTeam(note, location)) return false;
    if (normalizedText(note.section) !== normalizedText(location.section)) return false;
    if (normalizedText(note.row) !== normalizedText(location.row)) return false;
    return note.seatStart <= location.seatStart && note.seatEnd >= location.seatEnd;
  });

  matching.sort((left, right) => {
    const leftExact = left.seatStart === location.seatStart && left.seatEnd === location.seatEnd;
    const rightExact = right.seatStart === location.seatStart && right.seatEnd === location.seatEnd;
    if (leftExact !== rightExact) return leftExact ? -1 : 1;
    return (left.seatEnd - left.seatStart) - (right.seatEnd - right.seatStart);
  });

  return matching[0] || null;
};

const byTightestSeatRange = (location: Pick<SeatLocation, 'seatStart' | 'seatEnd'>) =>
  (left: SeatLevelNote, right: SeatLevelNote) => {
    const leftExact = left.seatStart === location.seatStart && left.seatEnd === location.seatEnd;
    const rightExact = right.seatStart === location.seatStart && right.seatEnd === location.seatEnd;
    if (leftExact !== rightExact) return leftExact ? -1 : 1;
    return (left.seatEnd - left.seatStart) - (right.seatEnd - right.seatStart);
  };

/**
 * Saved VL seat notes belong to a team name. The ticket's league category can be
 * the other team on the event, so a note matches when its team name is in the
 * event title or equals the requested team name.
 */
export const findSeatLevelNoteForListing = (
  notes: SeatLevelNote[],
  location: SeatLocation,
): SeatLevelNote | null => {
  const covering = notes.filter((note) => {
    if (normalizedText(note.userId) !== normalizedText(location.userId)) return false;
    if (!seatNoteMatchesTeam(note, location)) return false;
    if (normalizedText(note.section) !== normalizedText(location.section)) return false;
    if (normalizedText(note.row) !== normalizedText(location.row)) return false;
    return note.seatStart <= location.seatStart && note.seatEnd >= location.seatEnd;
  });
  covering.sort(byTightestSeatRange(location));
  return covering[0] || null;
};

/** One seat is CUSTOM:1. A larger odd group starts at 3. An even group starts at 2. */
export const buildCustomListingNote = (quantity: number | string | null | undefined): string => {
  const parsedQuantity = Number(quantity);
  if (!Number.isInteger(parsedQuantity) || parsedQuantity <= 0) return '';
  const start = parsedQuantity === 1 ? 1 : parsedQuantity % 2 === 0 ? 2 : 3;
  const values: number[] = [];
  for (let value = start; value <= parsedQuantity; value += 2) values.push(value);
  return values.length ? `CUSTOM:${values.join(',')}` : '';
};

const CUSTOM_NOTE_GAP = '    ';

export const prependCustomListingNote = (
  existing: string | null | undefined,
  custom: string,
): string => {
  const text = String(existing ?? '').trim();
  if (!custom) return text;
  if (!text) return custom;
  const upper = text.toUpperCase();
  const customUpper = custom.toUpperCase();
  if (upper === customUpper || upper.startsWith(`${customUpper} `)) return text;
  return `${custom}${CUSTOM_NOTE_GAP}${text}`;
};

export type ListingNoteLocation = {
  userId: string;
  teamId: string;
  teamName?: string;
  eventName?: string;
  section: string;
  row: string;
};

/** Saved notes for this user, team name, section, and row. Seat range is ignored. */
export const listingNoteChoices = (
  notes: SeatLevelNote[],
  locations: ListingNoteLocation[],
  field: 'internalNote' | 'externalNote',
): string[] => {
  const usable = locations.filter((location) => location.userId && location.section && location.row);
  const choices: string[] = [];
  for (const note of notes) {
    const matches = usable.some((location) => (
      normalizedText(note.userId) === normalizedText(location.userId)
      && normalizedText(note.section) === normalizedText(location.section)
      && normalizedText(note.row) === normalizedText(location.row)
      && seatNoteMatchesTeam(note, location)
    ));
    if (!matches) continue;
    const text = String(note[field] || '').trim();
    if (!text || choices.some((choice) => choice.toLowerCase() === text.toLowerCase())) continue;
    choices.push(text);
  }
  return choices;
};

/**
 * Dropdown choices while adding a note on a customer's profile. With a section and row they are the
 * saved notes for that exact spot (like the Verified to Listed popup). Before those are filled in,
 * every saved note this user has for the team is offered, so the list is never empty.
 */
export const profileNoteChoices = (
  notes: SeatLevelNote[],
  location: { userId: string; teamId?: string; teamName?: string; section?: string; row?: string },
  field: 'internalNote' | 'externalNote',
): string[] => {
  if (location.section?.trim() && location.row?.trim()) {
    return listingNoteChoices(notes, [{ ...location, teamId: location.teamId || '', section: location.section, row: location.row }], field);
  }
  const choices: string[] = [];
  for (const note of notes) {
    if (normalizedText(note.userId) !== normalizedText(location.userId)) continue;
    if (!(location.teamId || location.teamName) || !seatNoteMatchesTeam(note, location)) continue;
    const text = String(note[field] || '').trim();
    if (!text || choices.some((choice) => choice.toLowerCase() === text.toLowerCase())) continue;
    choices.push(text);
  }
  return choices;
};

/** An exact dropdown match keeps the saved note. Any other text is used as entered. */
export const resolveEnteredListingNote = (entered: string, options: string[]): string => {
  const text = String(entered ?? '').trim();
  if (!text) return '';
  const match = options.find((option) => option.trim().toLowerCase() === text.toLowerCase());
  return match ? match.trim() : text;
};

export const stripCustomListingPrefix = (value: string | null | undefined): string =>
  String(value ?? '').replace(/^CUSTOM:\d+(?:,\d+)*\s+/, '').trim();

export const listingCustomNoteForSeats = (seatNumbers: number[]): string => {
  const count = new Set(seatNumbers.filter((seat) => Number.isFinite(seat) && seat > 0)).size;
  return buildCustomListingNote(count);
};

/** Fixed ticket-detail phrases. Checking one appends it; unchecking removes only that phrase. */
export const LISTING_AMENITY_NOTES = [
  'Access to VIP Lounge',
  'Actual 1st row of the section',
  'Actual 2nd row of the section',
  'Actual 3rd row of the section',
  'Aisle seat',
  'Complimentary Food & Softdrinks',
  'Food and drink voucher included',
  'Includes complimentary/unlimited wine, beer, and liquor',
  'Includes unlimited drinks (beer, wine and liquor)',
  'Includes unlimited drinks (beer, wine, soda and water)',
  'Includes unlimited food and drinks (beer and soft drinks)',
  'Includes unlimited food and drinks (beer, wine and liquor)',
  'VIP Club Access w/ private entrance & restrooms',
] as const;

export const PARKING_INTERNAL_NOTE = 'Park @[Parking]';
export const PARKING_EXTERNAL_NOTE = 'Parking Pass Only';

export const isParkingPassRow = (row: unknown): boolean => normalizedText(row) === 'park';

export const isParkingPassNoteGroup = (rows: Array<string | null | undefined>): boolean =>
  rows.length > 0 && rows.every(isParkingPassRow);

const LISTING_NOTE_GAP = '  ';

/** Saved notes first, then the fixed phrases that are not already saved. */
export const withListingAmenityNotes = (choices: string[]): string[] => {
  const merged: string[] = [];
  const seen = new Set<string>();
  for (const choice of [...choices, ...LISTING_AMENITY_NOTES]) {
    const text = String(choice || '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) continue;
    seen.add(key);
    merged.push(text);
  }
  return merged;
};

const isListingNoteBoundary = (text: string, index: number, phrase: string): boolean => {
  const end = index + phrase.length;
  const beforeOk = index === 0 || /[\s|]/.test(text.charAt(index - 1));
  const rest = text.slice(end);
  const afterOk = rest.length === 0 || /^(\s{2,}|[|\n])/.test(rest);
  return beforeOk && afterOk;
};

/** Pull selected dropdown notes out of the text. Anything left is free-typed text. */
export const splitListingNotes = (
  value: string | null | undefined,
  options: string[],
): { base: string; selected: string[] } => {
  const phrases = options
    .map((option) => option.trim())
    .filter(Boolean)
    .sort((left, right) => right.length - left.length);
  let remaining = String(value ?? '').trim();
  const found = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const phrase of phrases) {
      const key = phrase.toLowerCase();
      if (found.has(key)) continue;
      let from = 0;
      while (from <= remaining.length) {
        const index = remaining.toLowerCase().indexOf(key, from);
        if (index === -1) break;
        if (!isListingNoteBoundary(remaining, index, phrase)) {
          from = index + 1;
          continue;
        }
        const left = remaining.slice(0, index).replace(/\s+$/, '');
        const right = remaining.slice(index + phrase.length).replace(/^\s+/, '');
        remaining = [left, right].filter(Boolean).join(LISTING_NOTE_GAP).trim();
        found.add(key);
        changed = true;
        break;
      }
      if (changed) break;
    }
  }
  return {
    base: remaining,
    selected: options
      .map((option) => option.trim())
      .filter((option) => option && found.has(option.toLowerCase())),
  };
};

export const appendListingNotes = (
  base: string | null | undefined,
  selected: string[],
  options: string[],
): string => {
  const selectedKeys = new Set(selected.map((phrase) => phrase.trim().toLowerCase()).filter(Boolean));
  const phrases = options
    .map((option) => option.trim())
    .filter((option) => option && selectedKeys.has(option.toLowerCase()));
  const text = String(base ?? '').trim();
  if (!phrases.length) return text;
  if (!text) return phrases.join(LISTING_NOTE_GAP);
  return `${text}${LISTING_NOTE_GAP}${phrases.join(LISTING_NOTE_GAP)}`;
};

/** Check adds the dropdown note. Uncheck removes only that note. A search prefix is cleared on check. */
export const toggleListingNote = (entered: string, options: string[], option: string): string => {
  const canonical = options.find((item) => item.trim().toLowerCase() === option.trim().toLowerCase())?.trim() || option.trim();
  if (!canonical) return String(entered ?? '').trim();
  const split = splitListingNotes(entered, options);
  const already = split.selected.some((item) => item.toLowerCase() === canonical.toLowerCase());
  const selected = already
    ? split.selected.filter((item) => item.toLowerCase() !== canonical.toLowerCase())
    : [...split.selected, canonical];
  let base = split.base;
  if (!already && base && canonical.toLowerCase().includes(base.toLowerCase())) base = '';
  return appendListingNotes(base, selected, options);
};

/** Exact saved note keeps its text. Any other entry, including a search with no result, is kept as typed. */
export const applyListingNoteDraft = (
  entered: string,
  options: string[],
  custom: string,
  field: 'internal' | 'external',
): { query: string; sent: string } => {
  const resolved = resolveEnteredListingNote(entered, options);
  const exact = Boolean(resolved) && options.some((option) => option.trim().toLowerCase() === String(entered ?? '').trim().toLowerCase());
  const query = exact ? resolved : entered;
  if (!resolved) return { query, sent: field === 'internal' ? custom : '' };
  return { query, sent: prependCustomListingNote(resolved, custom) };
};

/** Free-typed text plus every checked dropdown note, joined by two spaces. */
export const composeListingNoteDraft = (
  entered: string,
  options: string[],
  custom: string,
  field: 'internal' | 'external',
): { query: string; sent: string; selected: string[] } => {
  const split = splitListingNotes(entered, options);
  const body = appendListingNotes(split.base, split.selected, options);
  if (!body) {
    return { query: '', sent: field === 'internal' ? custom : '', selected: [] };
  }
  return { query: body, sent: prependCustomListingNote(body, custom), selected: split.selected };
};

export const seedListingNoteFields = (input: {
  notes: SeatLevelNote[];
  locations: ListingNoteLocation[];
  seatNumbers: number[];
  internalSeed: string;
  externalSeed: string;
}) => {
  const rows = input.locations.map((location) => location.row);
  if (isParkingPassNoteGroup(rows)) {
    return {
      internalChoices: [],
      externalChoices: [],
      customNote: '',
      internalQuery: PARKING_INTERNAL_NOTE,
      externalQuery: PARKING_EXTERNAL_NOTE,
      internalNotes: PARKING_INTERNAL_NOTE,
      externalNotes: PARKING_EXTERNAL_NOTE,
    };
  }
  const internalChoices = listingNoteChoices(input.notes, input.locations, 'internalNote');
  const externalChoices = listingNoteChoices(input.notes, input.locations, 'externalNote');
  const customNote = listingCustomNoteForSeats(input.seatNumbers);
  const internal = applyListingNoteDraft(stripCustomListingPrefix(input.internalSeed), internalChoices, customNote, 'internal');
  const external = applyListingNoteDraft(stripCustomListingPrefix(input.externalSeed), externalChoices, customNote, 'external');
  return {
    internalChoices,
    externalChoices,
    customNote,
    internalQuery: internal.query,
    externalQuery: external.query,
    internalNotes: internal.sent,
    externalNotes: external.sent,
  };
};

export const expandListingSeatNumbers = (seat: string | number | null | undefined): number[] => {
  const normalized = String(seat ?? '').trim().replace(/[–—]/g, '-').replace(/\s+/g, '');
  if (!normalized) return [];
  const rangeMatch = normalized.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2]);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= 0) return [];
    const values: number[] = [];
    for (let value = Math.min(start, end); value <= Math.max(start, end); value += 1) values.push(value);
    return values;
  }
  return normalized
    .split(',')
    .map((part) => Number(part))
    .filter((value) => Number.isFinite(value) && value > 0);
};

const contiguousRunQuantity = (seats: number[], seat: number): number => {
  const sorted = Array.from(new Set(seats.filter((value) => Number.isFinite(value)))).sort((a, b) => a - b);
  let runStart = 0;
  for (let index = 0; index < sorted.length; index += 1) {
    if (index === 0 || sorted[index] !== sorted[index - 1] + 1) runStart = index;
    if (sorted[index] === seat) {
      let runEnd = index;
      while (runEnd + 1 < sorted.length && sorted[runEnd + 1] === sorted[runEnd] + 1) runEnd += 1;
      return runEnd - runStart + 1;
    }
  }
  return 1;
};

export type ListingPopupNoteSeat = {
  section: string;
  row: string;
  seat: number;
  internalNote: string;
  externalNote: string;
};

/** Prefix each List on VL note with the CUSTOM note for that contiguous seat group. */
export const formatListingPopupNotes = (
  seats: ListingPopupNoteSeat[],
): { internalNotes: string; externalNotes: string } => {
  const seatsByLocation = new Map<string, number[]>();
  for (const seat of seats) {
    const key = `${normalizedText(seat.section)}|${normalizedText(seat.row)}`;
    const list = seatsByLocation.get(key) || [];
    list.push(seat.seat);
    seatsByLocation.set(key, list);
  }

  const internalNotes: string[] = [];
  const externalNotes: string[] = [];
  for (const seat of seats) {
    const key = `${normalizedText(seat.section)}|${normalizedText(seat.row)}`;
    const custom = buildCustomListingNote(contiguousRunQuantity(seatsByLocation.get(key) || [], seat.seat));
    internalNotes.push(prependCustomListingNote(seat.internalNote, custom));
    const external = String(seat.externalNote || '').trim();
    if (external) externalNotes.push(prependCustomListingNote(external, custom));
  }

  const unique = (values: string[]) =>
    Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).join('\n\n');
  return {
    internalNotes: unique(internalNotes),
    externalNotes: unique(externalNotes),
  };
};